import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Banknote, Search, Edit3, History, TrendingUp, IndianRupee } from "lucide-react";

const STATUS_COLORS = {
  "Not Required":         "#94A3B8",
  "Pending":              "#F59E0B",
  "Documents Submitted":  "#0EA5E9",
  "Bank Applied":         "#8B5CF6",
  "Approved":             "#10B981",
  "Disbursed":            "#059669",
  "Rejected":             "#E11D48",
};

export default function BankLinkage() {
  const [list, setList] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ bank_status: "Pending", bank_notes: "", bank_name: "", sanctioned_amount: "" });
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [yt, fn, st] = await Promise.all([
        api.get("/youth?limit=1000"),
        api.get("/dashboard/bank-funnel"),
        api.get("/bank/statuses"),
      ]);
      setList(yt.data.filter((y) => y.bank_linkage_required));
      setFunnel(fn.data);
      setStatuses(st.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => list.filter((y) => {
    if (statusFilter !== "all" && (y.bank_status || "Pending") !== statusFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      return y.full_name?.toLowerCase().includes(s) ||
             y.mobile?.includes(s) ||
             y.registration_no?.toLowerCase().includes(s) ||
             y.village?.toLowerCase().includes(s);
    }
    return true;
  }), [list, q, statusFilter]);

  const openEdit = (y) => {
    setEditing(y);
    setForm({
      bank_status: y.bank_status || "Pending",
      bank_notes: y.bank_notes || "",
      bank_name: y.bank_name || "",
      sanctioned_amount: y.sanctioned_amount || "",
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/youth/${editing.youth_id}/bank-status`, form);
      toast.success(`${editing.full_name} → ${form.bank_status}`);
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="bank-linkage-page">
      <div className="mb-6">
        <h1 className="font-heading font-bold text-3xl">Bank Linkage Workflow</h1>
        <p className="text-gray-600 text-sm">Track loan applications from intake to disbursement for the {funnel?.total_need_linkage || 0} youth who need it.</p>
      </div>

      {/* Funnel */}
      {funnel && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {funnel.by_status.map((s) => (
            <button
              key={s.status}
              onClick={() => setStatusFilter(s.status === statusFilter ? "all" : s.status)}
              className={`text-left bg-white rounded-2xl border p-4 transition-all hover:shadow-md ${
                statusFilter === s.status ? "border-[#1E3A8A] shadow-md" : "border-gray-100"
              }`}
              data-testid={`bank-funnel-${s.status}`}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white mb-2" style={{ backgroundColor: STATUS_COLORS[s.status] }}>
                <TrendingUp className="w-4 h-4" />
              </div>
              <div className="font-heading font-bold text-2xl">{s.count}</div>
              <div className="text-xs font-medium text-gray-700 leading-tight">{s.status}</div>
            </button>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-64">
          <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
          <Input className="pl-9" placeholder="Name / mobile / reg no / village" value={q} onChange={(e) => setQ(e.target.value)} data-testid="bank-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-56" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] overflow-hidden">
        {loading ? (
          <div className="p-10 text-gray-500 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-gray-500 text-center">
            No bank-linkage cases match this filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr className="text-xs uppercase tracking-wider text-gray-600">
                  <th className="p-3">Reg No.</th>
                  <th className="p-3">Name</th>
                  <th className="p-3">Village</th>
                  <th className="p-3">Loan amt</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Bank</th>
                  <th className="p-3">Sanctioned</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((y) => {
                  const status = y.bank_status || "Pending";
                  return (
                    <tr key={y.youth_id} className="border-t border-gray-100 hover:bg-gray-50/50" data-testid={`bank-row-${y.youth_id}`}>
                      <td className="p-3 font-mono text-xs">{y.registration_no}</td>
                      <td className="p-3 font-medium">{y.full_name}</td>
                      <td className="p-3">{y.village}</td>
                      <td className="p-3">{y.bank_loan_amount || "—"}</td>
                      <td className="p-3">
                        <Badge style={{ backgroundColor: STATUS_COLORS[status] }} className="text-white hover:opacity-90 border-0">
                          {status}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs">{y.bank_name || "—"}</td>
                      <td className="p-3 text-xs">{y.sanctioned_amount ? `₹ ${y.sanctioned_amount}` : "—"}</td>
                      <td className="p-3">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(y)} data-testid={`edit-bank-${y.youth_id}`}>
                          <Edit3 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Banknote className="w-5 h-5 text-[#10B981]" /> Update bank linkage
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-md p-3">
                <div className="font-heading font-semibold">{editing.full_name}</div>
                <div className="text-xs text-gray-500">{editing.registration_no} · {editing.village} · Asks for ₹{editing.bank_loan_amount || "—"}</div>
              </div>
              <div>
                <Label className="text-sm">Status</Label>
                <Select value={form.bank_status} onValueChange={(v) => setForm({ ...form, bank_status: v })}>
                  <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Bank name</Label>
                  <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} placeholder="e.g. SBI Pithapuram" data-testid="bank-name-input" />
                </div>
                <div>
                  <Label className="text-sm">Sanctioned ₹</Label>
                  <Input value={form.sanctioned_amount} onChange={(e) => setForm({ ...form, sanctioned_amount: e.target.value })} placeholder="e.g. 50,000" data-testid="sanctioned-amount-input" />
                </div>
              </div>
              <div>
                <Label className="text-sm">Notes</Label>
                <Textarea rows={3} value={form.bank_notes} onChange={(e) => setForm({ ...form, bank_notes: e.target.value })} placeholder="Documents pending, scheme name, follow-up date…" data-testid="bank-notes-input" />
              </div>

              {editing.bank_status_history?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1 flex items-center gap-1">
                    <History className="w-3 h-3" /> History
                  </div>
                  <div className="text-xs space-y-1 max-h-32 overflow-y-auto border rounded-md p-2 bg-gray-50">
                    {editing.bank_status_history.slice().reverse().slice(0, 6).map((h, i) => (
                      <div key={`hist-${h.updated_at || i}-${h.status}`} className="flex justify-between gap-2">
                        <span>
                          <Badge style={{ backgroundColor: STATUS_COLORS[h.status] }} className="text-white text-[10px] mr-1 border-0">{h.status}</Badge>
                          {h.notes && <span className="text-gray-600">{h.notes}</span>}
                        </span>
                        <span className="text-gray-400 text-[10px] shrink-0">{h.updated_at?.slice(0, 10)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#10B981] hover:bg-[#10B981]/90" data-testid="save-bank-status">
              <IndianRupee className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
